import { useRef, useCallback, useEffect } from 'react';

const BOTTOM_THRESHOLD = 50;

interface UseAutoScrollOptions {
  /** Dependency that triggers scroll check (e.g. messages array or streaming content) */
  deps: unknown[];
}

/** Keep a chat container scrolled to the bottom unless the user has scrolled up. */
export function useAutoScroll({ deps }: UseAutoScrollOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  const isAtBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD;
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    isAtBottomRef.current = true;
  }, []);

  const handleScroll = useCallback(() => {
    isAtBottomRef.current = isAtBottom();
  }, [isAtBottom]);

  useEffect(() => {
    if (isAtBottomRef.current) {
      scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { containerRef, scrollToBottom, handleScroll };
}
